import React, { useState, useEffect } from "react";
import { LegalLayout, P, Strong, UL, LI, Callout } from "./LegalLayout";

export default function LegalNotice() {
  const [lang, setLang] = useState(() => localStorage.getItem("nexum-lang") || "es");
  useEffect(() => {
    localStorage.setItem("nexum-lang", lang);
    window.scrollTo(0, 0);
  }, [lang]);

  const content = {
    es: {
      title: "Aviso Legal",
      subtitle:
        "Información legal preceptiva conforme a la Ley 34/2002 de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE).",
      lastUpdated: "Marzo 2026",
      sections: [
        {
          id: "titular",
          heading: "Datos identificativos del titular",
          body: (
            <>
              <P>
                En cumplimiento con el deber de información recogido en artículo 10 de la Ley 34/2002, se facilitan a continuación los siguientes datos:
              </P>
              <UL>
                <LI><Strong>Denominación social:</Strong> Nexum Business S.L.</LI>
                <LI><Strong>Nombre comercial:</Strong> Nexum</LI>
                <LI><Strong>Domicilio social:</Strong> Barcelona, España</LI>
                <LI><Strong>Correo electrónico:</Strong> hello@nexumbusiness.com</LI>
                <LI><Strong>Sitio web:</Strong> nexumbusiness.com</LI>
              </UL>
            </>
          ),
        },
        {
          id: "objeto",
          heading: "Objeto",
          body: (
            <P>
              El presente Aviso Legal regula el uso del sitio web nexumbusiness.com (en adelante, <Strong>&ldquo;el sitio web&rdquo;</Strong>), del que es titular Nexum Business S.L. La navegación por el sitio web atribuye la condición de Usuario e implica la aceptación plena y sin reservas del Aviso Legal desde el momento en que el Usuario acceda al sitio.
            </P>
          ),
        },
        {
          id: "condiciones",
          heading: "Condiciones de acceso y utilización",
          body: (
            <>
              <P>
                El Usuario se compromete a hacer un uso <Strong>diligente, correcto y lícito</Strong> del sitio web y a no emplearlo para realizar actividades ilícitas o contrarias al orden público, o a los derechos e intereses de Nexum o de terceros.
              </P>
              <P>
                El acceso al sitio web es libre y gratuito, sin perjuicio del coste de la conexión a Internet contratada por el Usuario. Determinados servicios pueden requerir suscripción, contratación o registro previo.
              </P>
            </>
          ),
        },
        {
          id: "responsabilidad",
          heading: "Responsabilidad",
          body: (
            <>
              <P>
                Nexum realiza sus mejores esfuerzos para evitar cualquier error en los contenidos que pudieran aparecer en el sitio web. Nexum no responde de las consecuencias que pudieran derivarse de:
              </P>
              <UL>
                <LI>Errores u omisiones en los contenidos.</LI>
                <LI>Falta de disponibilidad del sitio web (que se procurará minimizar).</LI>
                <LI>La transmisión de virus o programas maliciosos en los contenidos, a pesar de haber adoptado las medidas tecnológicas oportunas para evitarlo.</LI>
              </UL>
              <Callout>
                Nexum no será responsable por el uso del sitio web por parte de menores no autorizados por los adultos responsables.
              </Callout>
            </>
          ),
        },
        {
          id: "propiedad",
          heading: "Propiedad intelectual e industrial",
          body: (
            <>
              <P>
                El sitio web, incluyendo a título enunciativo pero no limitativo su programación, edición, compilación y demás elementos necesarios para su funcionamiento, los diseños, logotipos, texto y/o gráficos, son <Strong>propiedad de Nexum</Strong> o, en su caso, dispone de licencia o autorización expresa por parte de los autores.
              </P>
              <P>
                Todos los contenidos del sitio web se encuentran debidamente protegidos por la normativa de propiedad intelectual e industrial, así como inscritos en los registros públicos correspondientes.
              </P>
              <P>
                La reproducción, distribución y comunicación pública, incluida su modalidad de puesta a disposición, de la totalidad o parte de los contenidos, con fines comerciales y en cualquier soporte y por cualquier medio, sin la autorización previa, expresa y por escrito, quedan prohibidas.
              </P>
            </>
          ),
        },
        {
          id: "ley",
          heading: "Ley aplicable y jurisdicción",
          body: (
            <>
              <P>
                Para la resolución de cualquier controversia o cuestión relacionada con el sitio web o de las actividades en él desarrolladas será de aplicación la <Strong>legislación española</Strong>, a la que las partes se someten expresamente, siendo competentes para la resolución de todos los conflictos derivados o relacionados con su uso los <Strong>Juzgados y Tribunales de Barcelona</Strong>.
              </P>
              <P>
                Al acceder al sitio web, el Usuario declara que ha leído los términos de este Aviso Legal.
              </P>
            </>
          ),
        },
      ],
    },
    en: {
      title: "Legal Notice",
      subtitle:
        "Statutory legal information under Spanish Law 34/2002 on Information Society Services and Electronic Commerce (LSSI-CE).",
      lastUpdated: "March 2026",
      sections: [
        {
          id: "titular",
          heading: "Owner identification",
          body: (
            <>
              <P>
                In compliance with the duty of information set out in article 10 of Law 34/2002, the following data is provided:
              </P>
              <UL>
                <LI><Strong>Legal name:</Strong> Nexum Business S.L.</LI>
                <LI><Strong>Brand name:</Strong> Nexum</LI>
                <LI><Strong>Registered office:</Strong> Barcelona, Spain</LI>
                <LI><Strong>Email:</Strong> hello@nexumbusiness.com</LI>
                <LI><Strong>Website:</Strong> nexumbusiness.com</LI>
              </UL>
            </>
          ),
        },
        {
          id: "objeto",
          heading: "Purpose",
          body: (
            <P>
              This Legal Notice governs the use of the website nexumbusiness.com (hereinafter <Strong>&ldquo;the website&rdquo;</Strong>), owned by Nexum Business S.L. Browsing the website makes you a User and implies full and unreserved acceptance of this Legal Notice from the moment of access.
            </P>
          ),
        },
        {
          id: "condiciones",
          heading: "Conditions of access and use",
          body: (
            <>
              <P>
                The User undertakes to make a <Strong>diligent, correct and lawful</Strong> use of the website, and not to use it for unlawful activities, contrary to public order, or to the rights and interests of Nexum or third parties.
              </P>
              <P>
                Access to the site is free of charge, subject to the cost of the User's own Internet connection. Certain services may require subscription, contracting or prior registration.
              </P>
            </>
          ),
        },
        {
          id: "responsabilidad",
          heading: "Liability",
          body: (
            <>
              <P>
                Nexum makes its best efforts to avoid errors in the contents that may appear on the website. Nexum is not liable for consequences arising from:
              </P>
              <UL>
                <LI>Errors or omissions in the contents.</LI>
                <LI>Lack of availability of the site (which we strive to minimize).</LI>
                <LI>Transmission of viruses or malicious programs in the contents, despite the technological measures adopted to prevent it.</LI>
              </UL>
              <Callout>
                Nexum shall not be liable for use of the website by minors not authorized by their responsible adults.
              </Callout>
            </>
          ),
        },
        {
          id: "propiedad",
          heading: "Intellectual and industrial property",
          body: (
            <>
              <P>
                The website — including, without limitation, its programming, editing, compilation and other elements necessary for its operation, designs, logos, text and/or graphics — is <Strong>property of Nexum</Strong> or, where applicable, licensed or expressly authorized by the authors.
              </P>
              <P>
                All contents of the website are duly protected by intellectual and industrial property regulations, and registered in the corresponding public registries.
              </P>
              <P>
                Reproduction, distribution and public communication, including the making available of all or part of the contents for commercial purposes, in any medium and by any means, without prior express written authorization, is prohibited.
              </P>
            </>
          ),
        },
        {
          id: "ley",
          heading: "Applicable law and jurisdiction",
          body: (
            <>
              <P>
                For the resolution of any dispute or matter related to the website or activities carried out therein, <Strong>Spanish law</Strong> shall apply, to which the parties expressly submit, the competent courts being those of the <Strong>city of Barcelona</Strong>.
              </P>
              <P>By accessing the website, the User declares to have read the terms of this Legal Notice.</P>
            </>
          ),
        },
      ],
    },
  };

  const c = content[lang];
  return (
    <LegalLayout
      lang={lang}
      setLang={setLang}
      meta={{ title: c.title, subtitle: c.subtitle, lastUpdated: c.lastUpdated }}
      sections={c.sections}
      related={[
        { href: "/privacidad", label: lang === "es" ? "Política de Privacidad" : "Privacy Policy" },
        { href: "/terminos", label: lang === "es" ? "Términos de Uso" : "Terms of Use" },
        { href: "/cookies", label: lang === "es" ? "Política de Cookies" : "Cookie Policy" },
      ]}
    />
  );
}
